import { useEffect, useRef, useState } from "react";
import type { UniverseSearchResult } from "../../shared/types";
import { api } from "../lib/api";

/** Debounced Russell-1000 typeahead. Calls onSelect(ticker) when a row is chosen. */
export function TickerSearch({
  onSelect,
  placeholder = "Search ticker or company…",
  autoFocus = false,
}: {
  onSelect: (r: UniverseSearchResult) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UniverseSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    let cancel = false;
    const id = setTimeout(async () => {
      try {
        const { results } = await api.universe(q);
        if (!cancel) {
          setResults(results);
          setActive(0);
          setOpen(true);
        }
      } catch {
        /* ignore */
      }
    }, 180);
    return () => {
      cancel = true;
      clearTimeout(id);
    };
  }, [q]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function choose(r: UniverseSearchResult) {
    onSelect(r);
    setQ("");
    setResults([]);
    setOpen(false);
  }

  return (
    <div className="relative" ref={box}>
      <input
        autoFocus={autoFocus}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter" && results[active]) {
            e.preventDefault();
            choose(results[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        className="num w-full rounded-lg border border-edge bg-ink-900 px-3 py-2.5 text-fg outline-none placeholder:text-faint focus:border-accent"
      />
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-80 w-full overflow-auto rounded-lg border border-edge bg-ink-850 shadow-glow">
          {results.map((r, i) => (
            <button
              key={r.ticker}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(r)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left ${
                i === active ? "bg-ink-700" : ""
              } ${r.owned ? "opacity-45" : ""}`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="num w-16 shrink-0 font-bold text-fg">
                  {r.ticker}
                </span>
                <span className="truncate text-sm text-muted">{r.name}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {r.ownedLong && (
                  <span className="rounded bg-gain/15 px-1 text-[10px] font-bold text-gain">
                    L
                  </span>
                )}
                {r.ownedShort && (
                  <span className="rounded bg-loss/15 px-1 text-[10px] font-bold text-loss">
                    S
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
